/**
 * Sprint T-17.1 · Notification Center · MetricsTab.
 * SVG puro · 6 mini-gráficos derivados de las últimas 500 deliveries.
 * §4.8 · sin recharts · sin dependencias nuevas.
 */
import React, { useMemo } from "react";
import { WidgetCard, WidgetGrid, EmptyState, LoadingState, CitationBadge } from "../advisor";
import { useDeliveries } from "../../hooks/useNotifications";
import StatusPill, { ALL_DELIVERY_STATUSES } from "./shared/StatusPill";
import { ALL_CHANNELS } from "./shared/ChannelIcon";

const STATUS_COLORS = {
  sent: "#10b981",
  delivered: "#059669",
  failed: "#ef4444",
  queued: "#3b82f6",
  skipped_by_preference: "#64748b",
  skipped_no_provider: "#94a3b8",
  rate_limited: "#f59e0b",
  invalid_template: "#f97316",
  opened: "#8b5cf6",
  clicked: "#d946ef",
};

const CHANNEL_COLORS = {
  email: "#38bdf8",
  webhook: "#e879f9",
  sms: "#34d399",
  whatsapp: "#22c55e",
  push: "#fb923c",
};

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

function derive(items) {
  const byStatus = {};
  const byChannel = {};
  const byAttempt = {};
  const latencies = [];
  let dryRun = 0;
  const perHour = {};
  const now = Date.now();
  for (const d of items || []) {
    byStatus[d.status] = (byStatus[d.status] || 0) + 1;
    byChannel[d.channel] = (byChannel[d.channel] || 0) + 1;
    const a = d.attempt || 1;
    const bucket = a >= 4 ? "4+" : String(a);
    byAttempt[bucket] = (byAttempt[bucket] || 0) + 1;
    if (typeof d.latency_ms === "number" && d.latency_ms >= 0) latencies.push(d.latency_ms);
    if (d.dry_run) dryRun += 1;
    if (d.finished_at) {
      const t = new Date(d.finished_at).getTime();
      const ageH = Math.floor((now - t) / 3600000);
      if (ageH >= 0 && ageH < 24) {
        const key = 23 - ageH; // 0..23 · 0 = hace 23h, 23 = hace 0h
        perHour[key] = (perHour[key] || 0) + 1;
      }
    }
  }
  const hoursArr = Array.from({ length: 24 }, (_, i) => perHour[i] || 0);
  return {
    total: (items || []).length,
    byStatus, byChannel, byAttempt, latencies, dryRun, hoursArr,
  };
}

function latencyBuckets(list) {
  const bounds = [50, 200, 500, 1000, 2000, 5000];
  const labels = ["<50ms", "50-200", "200-500", "500ms-1s", "1-2s", "2-5s", ">5s"];
  const counts = Array(labels.length).fill(0);
  for (const v of list) {
    let idx = bounds.findIndex((b) => v < b);
    if (idx === -1) idx = bounds.length;
    counts[idx] += 1;
  }
  return { labels, counts };
}

// -------------------------------------------------------------------
// SVG · Horizontal Bar Histogram (Status)
// -------------------------------------------------------------------

function StatusHistogram({ byStatus, total }) {
  const entries = ALL_DELIVERY_STATUSES
    .map((s) => [s, byStatus[s] || 0])
    .filter(([_, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <EmptyState testId="notif-metrics-status-empty" />;
  const max = Math.max(...entries.map(([, n]) => n), 1);
  const barHeight = 18;
  const rowGap = 6;
  const height = entries.length * (barHeight + rowGap);
  return (
    <svg
      data-testid="notif-metrics-status-svg"
      viewBox={`0 0 320 ${height}`}
      preserveAspectRatio="xMinYMin meet"
      className="w-full"
      role="img"
      aria-label="Distribución por estado"
    >
      {entries.map(([s, n], i) => {
        const y = i * (barHeight + rowGap);
        const barMax = 140;
        const w = Math.max(2, (n / max) * barMax);
        const pctRaw = (n / total) * 100;
        const pctLabel = pctRaw >= 1 ? Math.round(pctRaw) : pctRaw.toFixed(1);
        return (
          <g key={s} data-testid={`notif-metrics-status-bar-${s}`}>
            <text x="0" y={y + 13} fontSize="10" fill="var(--ag-text-dim)" fontFamily="monospace">
              {s.slice(0, 13)}
            </text>
            <rect x="95" y={y} width={w} height={barHeight} fill={STATUS_COLORS[s] || "#64748b"} rx="2" />
            <text x="245" y={y + 13} fontSize="10" fill="var(--ag-text)" fontFamily="monospace">
              {n}
            </text>
            <text x="275" y={y + 13} fontSize="9" fill="var(--ag-text-dim)" fontFamily="monospace">
              {pctLabel}%
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// -------------------------------------------------------------------
// SVG · Donut (Channel distribution)
// -------------------------------------------------------------------

function ChannelDonut({ byChannel, total }) {
  const entries = ALL_CHANNELS
    .map((c) => [c, byChannel[c] || 0])
    .filter(([_, n]) => n > 0);
  if (entries.length === 0) return <EmptyState testId="notif-metrics-channel-empty" />;
  const sum = entries.reduce((a, [, n]) => a + n, 0);
  const cx = 60, cy = 60, R = 50, r = 30;
  let a0 = -Math.PI / 2;
  const arcs = entries.map(([ch, n]) => {
    const frac = n / sum;
    const a1 = a0 + frac * 2 * Math.PI;
    const large = frac > 0.5 ? 1 : 0;
    const x0 = cx + R * Math.cos(a0), y0 = cy + R * Math.sin(a0);
    const x1 = cx + R * Math.cos(a1), y1 = cy + R * Math.sin(a1);
    const xi0 = cx + r * Math.cos(a1), yi0 = cy + r * Math.sin(a1);
    const xi1 = cx + r * Math.cos(a0), yi1 = cy + r * Math.sin(a0);
    const path = `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${xi0} ${yi0} A ${r} ${r} 0 ${large} 0 ${xi1} ${yi1} Z`;
    a0 = a1;
    return { ch, n, path };
  });
  return (
    <div className="flex items-center gap-4">
      <svg
        data-testid="notif-metrics-channel-donut"
        viewBox="0 0 120 120"
        width="120"
        height="120"
        role="img"
        aria-label="Distribución por canal"
      >
        {arcs.map((a) => (
          <path key={a.ch} d={a.path} fill={CHANNEL_COLORS[a.ch] || "#64748b"} />
        ))}
        <text x="60" y="55" fontSize="16" textAnchor="middle" fill="var(--ag-text)" fontFamily="monospace">
          {sum}
        </text>
        <text x="60" y="70" fontSize="8" textAnchor="middle" fill="var(--ag-text-dim)">
          total
        </text>
      </svg>
      <ul className="space-y-1 text-[10px]">
        {arcs.map((a) => (
          <li key={a.ch} className="flex items-center gap-1.5" data-testid={`notif-metrics-channel-legend-${a.ch}`}>
            <span
              className="inline-block h-2 w-2 rounded"
              style={{ background: CHANNEL_COLORS[a.ch] || "#64748b" }}
            />
            <span className="font-mono uppercase">{a.ch}</span>
            <span className="text-[var(--ag-text-dim)]">{a.n}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------------------------------------------------------------------
// SVG · Latency histogram
// -------------------------------------------------------------------

function LatencyHistogram({ latencies }) {
  if (latencies.length === 0) return <EmptyState testId="notif-metrics-latency-empty" />;
  const { labels, counts } = latencyBuckets(latencies);
  const max = Math.max(...counts, 1);
  const barW = 32, gap = 6, h = 100;
  const svgW = labels.length * (barW + gap);
  return (
    <svg
      data-testid="notif-metrics-latency-svg"
      viewBox={`0 0 ${svgW} ${h + 24}`}
      preserveAspectRatio="xMinYMin meet"
      className="w-full"
      role="img"
      aria-label="Distribución de latencias"
    >
      {labels.map((lbl, i) => {
        const value = counts[i];
        const bh = (value / max) * h;
        const x = i * (barW + gap);
        return (
          <g key={lbl} data-testid={`notif-metrics-latency-bar-${i}`}>
            <rect x={x} y={h - bh} width={barW} height={bh} fill="#38bdf8" rx="2" />
            <text x={x + barW / 2} y={h - bh - 3} fontSize="9" textAnchor="middle" fill="var(--ag-text)">
              {value}
            </text>
            <text x={x + barW / 2} y={h + 12} fontSize="8" textAnchor="middle" fill="var(--ag-text-dim)">
              {lbl}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// -------------------------------------------------------------------
// SVG · Hourly line chart
// -------------------------------------------------------------------

function HourlyLine({ hoursArr }) {
  if (hoursArr.every((v) => v === 0)) return <EmptyState testId="notif-metrics-hourly-empty" />;
  const max = Math.max(...hoursArr, 1);
  const W = 400, H = 100;
  const stepX = W / (hoursArr.length - 1);
  const points = hoursArr
    .map((v, i) => `${(i * stepX).toFixed(1)},${(H - (v / max) * (H - 10) - 5).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      data-testid="notif-metrics-hourly-svg"
      viewBox={`0 0 ${W} ${H + 16}`}
      preserveAspectRatio="xMinYMin meet"
      className="w-full"
      role="img"
      aria-label="Entregas por hora (últimas 24h)"
    >
      <polyline
        fill="rgba(56, 189, 248, 0.15)"
        stroke="#38bdf8"
        strokeWidth="1.5"
        points={`0,${H - 5} ${points} ${W},${H - 5}`}
      />
      <polyline fill="none" stroke="#38bdf8" strokeWidth="1.5" points={points} />
      <text x="0" y={H + 14} fontSize="8" fill="var(--ag-text-dim)">-24h</text>
      <text x={W - 20} y={H + 14} fontSize="8" fill="var(--ag-text-dim)">ahora</text>
    </svg>
  );
}

// -------------------------------------------------------------------
// SVG · Dry-run ratio
// -------------------------------------------------------------------

function DryRunRatio({ dryRun, total }) {
  if (total === 0) return <EmptyState testId="notif-metrics-dryrun-empty" />;
  const pct = Math.round((dryRun / total) * 100);
  const R = 40, cx = 50, cy = 50;
  const circumference = 2 * Math.PI * R;
  const dash = (pct / 100) * circumference;
  return (
    <div className="flex items-center gap-4">
      <svg
        data-testid="notif-metrics-dryrun-ratio"
        viewBox="0 0 100 100"
        width="100"
        height="100"
        role="img"
        aria-label="Ratio dry-run"
      >
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="var(--ag-border)" strokeWidth="8" />
        <circle
          cx={cx} cy={cy} r={R}
          fill="none"
          stroke="#f59e0b"
          strokeWidth="8"
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeDashoffset={circumference / 4}
          transform="rotate(-90 50 50)"
        />
        <text x="50" y="50" fontSize="16" textAnchor="middle" fill="var(--ag-text)" fontFamily="monospace">
          {pct}%
        </text>
        <text x="50" y="65" fontSize="7" textAnchor="middle" fill="var(--ag-text-dim)">
          dry-run
        </text>
      </svg>
      <div className="text-[10px] text-[var(--ag-text-dim)]">
        <div>{dryRun} de {total} entregas</div>
        <div className="mt-0.5">
          {pct === 100 && <span className="text-emerald-500">Rollout §17.6 activo</span>}
          {pct > 0 && pct < 100 && <span className="text-amber-500">Rollout parcial</span>}
          {pct === 0 && <span className="text-emerald-500">Envío real</span>}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// SVG · Retries heatmap (attempt distribution)
// -------------------------------------------------------------------

function RetriesHeatmap({ byAttempt }) {
  const keys = ["1", "2", "3", "4+"];
  const values = keys.map((k) => byAttempt[k] || 0);
  const max = Math.max(...values, 1);
  const W = 200, H = 50;
  const boxW = 44, boxH = 44, gap = 8;
  return (
    <svg
      data-testid="notif-metrics-retries"
      viewBox={`0 0 ${(boxW + gap) * keys.length - gap} ${boxH + 14}`}
      preserveAspectRatio="xMinYMin meet"
      width="220"
      height="60"
      role="img"
      aria-label="Distribución de reintentos"
    >
      {keys.map((k, i) => {
        const v = values[i];
        const intensity = v / max;
        const alpha = 0.2 + 0.8 * intensity;
        const color = k === "1"
          ? `rgba(16, 185, 129, ${alpha})`
          : k === "2"
          ? `rgba(59, 130, 246, ${alpha})`
          : k === "3"
          ? `rgba(245, 158, 11, ${alpha})`
          : `rgba(239, 68, 68, ${alpha})`;
        const x = i * (boxW + gap);
        return (
          <g key={k} data-testid={`notif-metrics-retry-box-${k}`}>
            <rect x={x} y="0" width={boxW} height={boxH} fill={color} stroke="var(--ag-border)" strokeWidth="0.5" rx="4" />
            <text x={x + boxW / 2} y={boxH / 2 + 2} fontSize="12" textAnchor="middle" fill="var(--ag-text)" fontFamily="monospace">
              {v}
            </text>
            <text x={x + boxW / 2} y={boxH + 10} fontSize="8" textAnchor="middle" fill="var(--ag-text-dim)">
              attempt {k}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// -------------------------------------------------------------------
// Compose
// -------------------------------------------------------------------

export default function MetricsTab() {
  const q = useDeliveries({ limit: 500 });
  const stats = useMemo(() => derive(q.data?.items || []), [q.data]);

  return (
    <section className="space-y-4" data-testid="notif-metrics-tab">
      <div
        data-testid="notif-metrics-note"
        className="rounded border border-indigo-500/30 bg-indigo-500/5 p-2 text-[10px] text-indigo-400"
      >
        Métricas derivadas de las últimas <strong>500 entregas</strong> · el backend no expone endpoint dedicado
        (frozen). Endpoint T-18 previsto.
      </div>

      {q.isLoading && <LoadingState />}
      {q.error && <EmptyState message="No se pudieron cargar métricas" testId="notif-metrics-error" />}
      {q.data && stats.total === 0 && (
        <EmptyState
          title="Sin entregas todavía"
          message="Espera al primer sweep del dispatcher."
          testId="notif-metrics-empty"
        />
      )}

      {stats.total > 0 && (
        <>
          <WidgetGrid cols="md:grid-cols-2 lg:grid-cols-3">
            <WidgetCard
              title="Deliveries por estado"
              description={`${stats.total} entregas · barras horizontales`}
              testId="widget-notif-metrics-status"
            >
              <StatusHistogram byStatus={stats.byStatus} total={stats.total} />
            </WidgetCard>

            <WidgetCard
              title="Distribución por canal"
              description="Donut chart"
              testId="widget-notif-metrics-channel"
            >
              <ChannelDonut byChannel={stats.byChannel} total={stats.total} />
            </WidgetCard>

            <WidgetCard
              title="Ratio dry-run"
              description="§17.6 · rollout progresivo"
              testId="widget-notif-metrics-dryrun"
            >
              <DryRunRatio dryRun={stats.dryRun} total={stats.total} />
            </WidgetCard>
          </WidgetGrid>

          <WidgetGrid cols="md:grid-cols-2 lg:grid-cols-2">
            <WidgetCard
              title="Latencia · histograma"
              description={`${stats.latencies.length} entregas con latencia registrada`}
              testId="widget-notif-metrics-latency"
            >
              <LatencyHistogram latencies={stats.latencies} />
            </WidgetCard>

            <WidgetCard
              title="Actividad por hora (últimas 24h)"
              description="Line chart"
              testId="widget-notif-metrics-hourly"
            >
              <HourlyLine hoursArr={stats.hoursArr} />
            </WidgetCard>
          </WidgetGrid>

          <WidgetCard
            title="Reintentos (attempt distribution)"
            description="Cada bloque cuenta entregas con ese attempt"
            testId="widget-notif-metrics-retries"
          >
            <RetriesHeatmap byAttempt={stats.byAttempt} />
          </WidgetCard>
        </>
      )}

      <footer className="border-t border-[var(--ag-border)] pt-3">
        <CitationBadge sources={["Derivado de /api/notifications/deliveries?limit=500 · sin dependencias"]} />
      </footer>
    </section>
  );
}
